/**
 * Phase 9-D — Email approve/deny landing page (PUBLIC).
 *
 * The Resend email channel can't carry native action buttons that POST
 * back, so the email body contains two click-through links of the form:
 *
 *   {APP_URL}/email-approve/{proposal_id}?action=approve&token=…
 *   {APP_URL}/email-approve/{proposal_id}?action=deny&token=…
 *
 * (Pre-cleanup this page lived at `/agency/email-approval/:id`. The
 * route was relocated when the parallel `/agency/*` portal was retired.
 * Backend templates must be updated to emit the new URL — there is no
 * in-app redirect for the old path.)
 *
 * This page:
 *   1. Reads `:id` from the URL path and `action` + `token` from the
 *      query string.
 *   2. POSTs to `/api/v2/internal/actions/{id}/verify-email-token` to
 *      preview the proposal (no state change).
 *   3. If the token is valid, shows the proposal summary + the matching
 *      Approve OR Deny button.
 *   4. Clicking the button POSTs to
 *      `/api/v2/internal/actions/{id}/act-via-email-token` — the same
 *      verifier re-runs on the backend and then approveWithActor /
 *      denyWithActor fires.
 *
 * Auth:
 *   - NONE required. The signed HMAC token IS the credential.
 *   - If the user happens to be logged in (Phase 11-Bridge stores the
 *     agency employee key under `state.auth.agency.accessToken`), the
 *     existing Bearer is sent and the backend prefers the employee
 *     identity (records actor as `user:agency:{id}`). Otherwise the
 *     actor is recorded as `chat:email:{recipient_email}` so the audit
 *     log captures who clicked.
 *
 * Route mounting: registered as a top-level PUBLIC route in `App.tsx`
 * (no auth wrapper). The splash / fetchAppInit / token-expiry effects
 * in App.tsx skip any path starting with `/email-approve` so this page
 * renders without dragging the seller-onboarding chrome.
 */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { RootState } from '../../store/store';
import { CENTRAL_API_BASE_URL_V2 } from '../../lib/constants/central-api';

type EmailAction = 'approve' | 'deny';

interface ProposalPreview {
  id: string;
  status: string;
  tool_name?: string | null;
  platform?: string | null;
  category?: string | null;
  entity_id?: string | null;
  entity_name?: string | null;
  risk_level?: string | null;
  reasoning?: string | null;
  current_value?: unknown;
  proposed_value?: unknown;
  created_at?: string | null;
  shop?: { id: string | number; slug: string; name: string } | null;
  action_type?: { id: string; action_key: string; name: string } | null;
}

interface VerifyResponse {
  valid: boolean;
  error?: string;
  recipient_email?: string | null;
  expires_at?: number | null;
  proposal?: ProposalPreview;
}

interface ActResponse {
  message: 'approved' | 'denied';
  proposal_id: string;
  status: string;
  recipient_email: string;
  actor: string;
}

type Phase = 'verifying' | 'ready' | 'submitting' | 'done' | 'error';

const TERMINAL_STATUSES = new Set([
  'approved',
  'denied',
  'executing',
  'completed',
  'failed',
]);

const ERROR_COPY: Record<string, string> = {
  expired: 'This link has expired. Email approval links are valid for 24 hours — ask the sender to re-trigger the proposal so a fresh email lands.',
  bad_signature: 'This link has been tampered with or is no longer valid. Please contact your agency owner.',
  mismatch: 'This link does not match the proposal in the URL. Please use the most recent email.',
  malformed: 'This link is malformed. Please use the most recent email.',
  no_secret: 'Email approvals are not configured on the server. Contact support.',
  unsupported_action: 'Unsupported action — only Approve and Deny are valid.',
};

/**
 * Wrap fetch with the optional agency Bearer token so the backend can
 * promote actor to `user:agency:{id}` when the user happens to be
 * logged in. Never throws on a missing token — the token is OPTIONAL.
 */
async function postEmailEndpoint<T>(
  url: string,
  body: Record<string, unknown>,
  bearer: string
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  if (bearer) {
    headers.Authorization = `Bearer ${bearer}`;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // fall through
    }
  }

  if (!response.ok) {
    const detail =
      (parsed as { message?: string; error?: string })?.message ||
      (parsed as { error?: string })?.error ||
      `Request failed (${response.status} ${response.statusText})`;
    const err = new Error(detail) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  // Backend wraps in ApiResponse `{ success, data }` — unwrap if present.
  const wrapped = parsed as { data?: T } | null;
  if (wrapped && typeof wrapped === 'object' && 'data' in wrapped) {
    return wrapped.data as T;
  }
  return parsed as T;
}

function safeStringify(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

const EmailApprovalLanding: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const action = (searchParams.get('action') as EmailAction | null) || null;
  const token = searchParams.get('token') || '';

  // Optional agency token — if present, the backend prefers the employee
  // identity for the actor. Missing token is fine.
  const agencyBearer = useSelector(
    (s: RootState) => s.auth.agency.accessToken || ''
  );
  const employeeIdentity = useSelector(
    (s: RootState) => s.auth.agency.employeeIdentity
  );

  const [phase, setPhase] = useState<Phase>('verifying');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [proposal, setProposal] = useState<ProposalPreview | null>(null);
  const [recipientEmail, setRecipientEmail] = useState<string | null>(null);
  const [actResult, setActResult] = useState<ActResponse | null>(null);

  useEffect(() => {
    if (!id) {
      setPhase('error');
      setErrorMessage('Missing proposal id in URL.');
      return;
    }
    if (!action || (action !== 'approve' && action !== 'deny')) {
      setPhase('error');
      setErrorMessage(
        'Missing or invalid ?action=approve|deny in URL.'
      );
      return;
    }
    if (!token) {
      setPhase('error');
      setErrorMessage('Missing ?token=… in URL.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const verifyUrl = `${CENTRAL_API_BASE_URL_V2}internal/actions/${encodeURIComponent(
          id
        )}/verify-email-token`;
        const res = await postEmailEndpoint<VerifyResponse>(
          verifyUrl,
          { token, action },
          agencyBearer
        );
        if (cancelled) return;

        if (!res?.valid) {
          const code = res?.error || 'unknown';
          setErrorCode(code);
          setErrorMessage(
            ERROR_COPY[code] ||
              'This link could not be verified. Please use the most recent email.'
          );
          setPhase('error');
          return;
        }

        setProposal(res.proposal || null);
        setRecipientEmail(res.recipient_email || null);
        setPhase('ready');
      } catch (err: any) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('[EmailApprovalLanding] verify failed', err);
        setErrorMessage(
          err?.message ||
            'Could not verify your link. Please try again or contact support.'
        );
        setPhase('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, action, token, agencyBearer]);

  const submit = async () => {
    if (!id || !action) return;
    setPhase('submitting');
    setErrorMessage(null);

    try {
      const actUrl = `${CENTRAL_API_BASE_URL_V2}internal/actions/${encodeURIComponent(
        id
      )}/act-via-email-token`;
      const res = await postEmailEndpoint<ActResponse>(
        actUrl,
        { token, action },
        agencyBearer
      );
      setActResult(res);
      setPhase('done');
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[EmailApprovalLanding] act failed', err);
      const isConflict = err?.status === 409;
      setErrorMessage(
        isConflict
          ? 'This proposal has already been processed.'
          : err?.message || 'Could not record your decision. Please try again.'
      );
      setPhase('error');
    }
  };

  const isSignedIn = !!agencyBearer && !!employeeIdentity;
  const verb = action === 'approve' ? 'Approve' : 'Deny';
  const verbPast = action === 'approve' ? 'approved' : 'denied';

  return (
    <Container maxWidth='sm' sx={{ py: 6 }}>
      <Paper elevation={1} sx={{ p: 4 }}>
        {phase === 'verifying' && (
          <Stack spacing={2} alignItems='center' sx={{ py: 2 }}>
            <CircularProgress size={28} />
            <Typography variant='body2' color='text.secondary'>
              Verifying your link…
            </Typography>
          </Stack>
        )}

        {phase === 'error' && (
          <Stack spacing={2}>
            <Typography variant='h5' component='h1'>
              Link cannot be used
            </Typography>
            <Alert severity='error'>
              {errorMessage ||
                'This link could not be verified. Please use the most recent email.'}
            </Alert>
            {errorCode === 'expired' && (
              <Typography variant='body2' color='text.secondary'>
                Email approval links expire after 24 hours. Ask the sender
                to re-trigger the proposal so a fresh email lands.
              </Typography>
            )}
            <Box>
              <Button
                component={RouterLink}
                to='/dashboard'
                variant='outlined'
                size='small'
              >
                Go to dashboard
              </Button>
            </Box>
          </Stack>
        )}

        {(phase === 'ready' || phase === 'submitting') && proposal && (
          <Stack spacing={3}>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
            >
              <Typography variant='h5' component='h1'>
                {verb} this proposal?
              </Typography>
              <Chip
                label={proposal.status.replace(/_/g, ' ')}
                size='small'
                color={
                  proposal.status === 'proposed' ||
                  proposal.status === 'pending_review'
                    ? 'primary'
                    : 'default'
                }
              />
            </Stack>

            {recipientEmail && (
              <Alert severity='info' variant='outlined'>
                You are acting as <strong>{recipientEmail}</strong>
                {isSignedIn && employeeIdentity?.email
                  ? ` (signed in as ${employeeIdentity.email}).`
                  : ' via email link.'}
              </Alert>
            )}

            {proposal.status &&
              TERMINAL_STATUSES.has(proposal.status) && (
                <Alert severity='warning'>
                  This proposal has already been processed (
                  {proposal.status.replace(/_/g, ' ')}). Your click won't
                  change anything.
                </Alert>
              )}

            <List dense>
              {proposal.tool_name && (
                <ListItem disableGutters>
                  <ListItemText
                    primary='Tool'
                    secondary={proposal.tool_name}
                  />
                </ListItem>
              )}
              {proposal.platform && (
                <ListItem disableGutters>
                  <ListItemText
                    primary='Platform'
                    secondary={proposal.platform}
                  />
                </ListItem>
              )}
              {proposal.entity_name && (
                <ListItem disableGutters>
                  <ListItemText
                    primary='Entity'
                    secondary={proposal.entity_name}
                  />
                </ListItem>
              )}
              {proposal.shop?.name && (
                <ListItem disableGutters>
                  <ListItemText
                    primary='Brand'
                    secondary={`${proposal.shop.name} (${proposal.shop.slug})`}
                  />
                </ListItem>
              )}
              {proposal.risk_level && (
                <ListItem disableGutters>
                  <ListItemText
                    primary='Risk'
                    secondary={proposal.risk_level}
                  />
                </ListItem>
              )}
            </List>

            {proposal.reasoning && (
              <Box>
                <Typography variant='subtitle2' gutterBottom>
                  Reasoning
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {proposal.reasoning}
                </Typography>
              </Box>
            )}

            {(proposal.current_value != null ||
              proposal.proposed_value != null) && (
              <Box>
                <Typography variant='subtitle2' gutterBottom>
                  Change
                </Typography>
                {proposal.current_value != null && (
                  <Box
                    component='pre'
                    sx={{
                      bgcolor: 'action.hover',
                      p: 1,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      maxHeight: 160,
                    }}
                  >
                    {`current: ${safeStringify(proposal.current_value)}`}
                  </Box>
                )}
                {proposal.proposed_value != null && (
                  <Box
                    component='pre'
                    sx={{
                      bgcolor: 'action.hover',
                      p: 1,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      maxHeight: 160,
                      mt: 1,
                    }}
                  >
                    {`proposed: ${safeStringify(proposal.proposed_value)}`}
                  </Box>
                )}
              </Box>
            )}

            <Divider />

            <Stack direction='row' spacing={1} justifyContent='flex-end'>
              <Button
                component={RouterLink}
                to='/actions/queue'
                variant='text'
                size='small'
              >
                Open in dashboard instead
              </Button>
              <Button
                variant='contained'
                color={action === 'approve' ? 'primary' : 'error'}
                disabled={
                  phase === 'submitting' ||
                  TERMINAL_STATUSES.has(proposal.status)
                }
                onClick={submit}
                startIcon={
                  phase === 'submitting' ? (
                    <CircularProgress size={16} color='inherit' />
                  ) : undefined
                }
              >
                {phase === 'submitting' ? `${verb}ing…` : verb}
              </Button>
            </Stack>

            {!isSignedIn && (
              <Typography variant='caption' color='text.secondary'>
                Tip — sign in to the agency portal first to have this
                decision recorded against your employee identity instead
                of <code>chat:email:{recipientEmail || 'you'}</code>.
              </Typography>
            )}
          </Stack>
        )}

        {phase === 'done' && actResult && (
          <Stack spacing={2}>
            <Typography variant='h5' component='h1'>
              Proposal {verbPast}
            </Typography>
            <Alert severity='success'>
              Your decision has been recorded. Status is now{' '}
              <strong>{actResult.status.replace(/_/g, ' ')}</strong>.
            </Alert>
            <Typography variant='body2' color='text.secondary'>
              Recorded actor: <code>{actResult.actor}</code>
            </Typography>
            <Box>
              <Button
                component={RouterLink}
                to='/actions/queue'
                variant='outlined'
                size='small'
              >
                View in dashboard
              </Button>
            </Box>
          </Stack>
        )}
      </Paper>
    </Container>
  );
};

export default EmailApprovalLanding;
